import {useEffect, useState} from 'react';

import dashboardDesktop from '../assets/images/dashboard_desktop.webp';
import dashboardMobile from '../assets/images/dashboard_mobile.webp';
import patientsDesktop from '../assets/images/patients_desktop.webp';
import patientsMobile from '../assets/images/patients_mobile.webp';
import waitinglistDesktop from '../assets/images/waitinglist_desktop.webp';
import waitinglistMobile from '../assets/images/waitinglist_mobile.webp';
import appointmentDesktop from '../assets/images/appointment_desktop.webp';
import appointmentMobile from '../assets/images/appointment_mobile.webp';
import paymentsDesktop from '../assets/images/payments_desktop.webp';
import paymentsMobile from '../assets/images/payments_mobile.webp';
import treatmentsDesktop from '../assets/images/treatments_desktop.webp';
import treatmentsMobile from '../assets/images/treatments_mobile.webp';
import dentalChartDesktop from '../assets/images/dentalChart_desktop.webp';
import dentalChartMobile from '../assets/images/dentalChart_mobile.webp';

import {Zap, ChevronLeft, ChevronRight, CircleCheck} from "lucide-react";

const features = [
    {
        id: 1,
        badge: "Liste des patients",
        title: "Gérez vos patients efficacement",
        items: [
            {
                title: "Dossiers patients complets",
                description: "Accédez rapidement à l'historique médical, traitements en cours et informations personnelles de chaque patient."
            },
            {
                title: "Recherche et filtres avancés",
                description: "Trouvez instantanément un patient grâce à la recherche intelligente et aux filtres personnalisables."
            }
        ],
        desktopImage: patientsDesktop,
        mobileImage: patientsMobile
    },
    {
        id: 2,
        badge: "Salle d'attente",
        title: "Suivez votre salle d'attente en temps réel",
        items: [
            {
                title: "Liste d'attente en direct",
                description: "Visualisez tous les patients présents dans votre salle d'attente avec l'heure d'arrivée de chacun."
            }
        ],
        desktopImage: waitinglistDesktop,
        mobileImage: waitinglistMobile
    },
    {
        id: 3,
        badge: "Schéma dentaire",
        title: "Diagnostic visuel et précis",
        items: [
            {
                title: "Schéma interactif",
                description: "Visualisez et annotez chaque dent avec un schéma dentaire complet et intuitif pour un suivi optimal."
            },
            {
                title: "Historique des soins",
                description: "Consultez l'historique de tous les traitements effectués sur chaque dent avec dates et détails des interventions."
            }
        ],
        desktopImage: dentalChartDesktop,
        mobileImage: dentalChartMobile
    },
    {
        id: 4,
        badge: "Calendrier intelligent",
        title: "Planifiez vos rendez-vous simplement",
        items: [
            {
                title: "Calendrier intelligent",
                description: "Vue journalière, hebdomadaire ou mensuelle de tous vos rendez-vous avec rappels automatiques pour vos patients."
            },
            {
                title: "Prise de rendez-vous rapide",
                description: "Créez un nouveau rendez-vous en quelques clics avec détection automatique des créneaux disponibles."
            }
        ],
        desktopImage: appointmentDesktop,
        mobileImage: appointmentMobile
    },
    {
        id: 5,
        badge: "Traitements",
        title: "Traitements personnalisés",
        items: [
            {
                title: "Création de traitements",
                description: "Enregistrer vos traitements, affecter une couleure et un prix personnalisé pour chacun."
            }
        ],
        desktopImage: treatmentsDesktop,
        mobileImage: treatmentsMobile
    },
    {
        id: 6,
        badge: "Paiements",
        title: "Gérez vos finances en toute simplicité",
        items: [
            {
                title: "Suivi des paiements",
                description: "Enregistrez tous les paiements, visualisez les impayés et suivez votre chiffre d'affaires quotidien."
            }
        ],
        desktopImage: paymentsDesktop,
        mobileImage: paymentsMobile
    },
    {
        id: 7,

        badge: "Tableau de bord",
        title: "Une expérience fluide, sur ordinateur comme sur mobile",
        items: [
            {
                title: "Vue sur ordinateur",
                description: "Vision globale de l'activité du jour : patients en salle d'attente, prochains rendez-vous et chiffre d'affaires collecté en temps réel."
            },
            {
                title: "Accès sur mobile et tablette",
                description: "Gardez le contact avec votre cabinet où que vous soyez. L'application s'adapte parfaitement à votre smartphone."
            }
        ],
        desktopImage: dashboardDesktop,
        mobileImage: dashboardMobile
    }
];

function SoftwarePreview() {
    const [currentIndex, setCurrentIndex] = useState(0);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % features.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + features.length) % features.length);
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    useEffect(() => {
        const timer = setInterval(nextSlide, 10000);
        return () => clearInterval(timer);
    }, []);

    return (
        <section id="preview" className="py-12 px-4 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                    <h2 className="text-primary font-bold tracking-widest uppercase text-sm mb-4">Aperçu du logiciel</h2>
                    <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900">Découvrez l'interface de votre futur quotidien</h3>
                </div>
                {/* Slider container */}
                <div className="relative">
                    <div className="overflow-hidden">
                        <div
                            className="flex transition-transform duration-500 ease-in-out"
                            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                        >
                            {features.map((feature, index) => {
                                const isReversed = index % 2 === 1;

                                return (
                                    <div key={feature.id} className="w-full flex-shrink-0">
                                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                                            {/* Text Content - Order changes based on isReversed */}
                                            <div className={`space-y-10 ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                                                <div className="space-y-4">
                                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-bold">
                                                        <Zap size={16} className="fill-blue-600" />
                                                        {feature.badge}
                                                    </div>
                                                    <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">
                                                        {feature.title}
                                                    </h3>
                                                </div>

                                                <div className="space-y-4">
                                                    {feature.items.map((item, idx) => (
                                                        <div key={idx} className="flex gap-4">
                                                            <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 font-bold">
                                                                <CircleCheck />
                                                            </div>
                                                            <div>
                                                                <h4 className="text-xl font-bold text-slate-900 mb-2">
                                                                    {item.title}
                                                                </h4>
                                                                <p className="text-slate-600 leading-relaxed">
                                                                    {item.description}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Screens - Order changes based on isReversed */}
                                            <div className={`relative flex md:block gap-4 items-end ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                                                {/* Mobile Mockup */}
                                                <div className={`md:absolute md:-bottom-6 ${isReversed ? 'md:-right-16' : 'md:-left-16'} w-28 md:w-36 md:z-20 transform ${isReversed ? 'md:-rotate-6' : 'md:rotate-6'} hover:rotate-0 transition-transform duration-300 flex-shrink-0`}>
                                                    <div className="bg-slate-800 rounded-[1.75rem] p-1 border border-slate-900">
                                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-2.5 bg-slate-800 rounded-b-2xl z-30"></div>
                                                        <div className="bg-white rounded-[1.25rem] overflow-hidden aspect-[9/19]">
                                                            <img
                                                                src={feature.mobileImage}
                                                                alt="Mobile app preview"
                                                                className="w-full object-cover"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="relative z-10 flex-1">
                                                    {/* Desktop Mockup */}
                                                    <div className="bg-slate-800 rounded-t-xl p-1 flex gap-1.5 items-center">
                                                        <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                                                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                                                        <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                                                    </div>
                                                    <div className="bg-white rounded-b-xl border border-slate-200 p-1 overflow-hidden aspect-[4/2]">
                                                        <img
                                                            src={feature.desktopImage}
                                                            alt="Desktop dashboard preview"
                                                            className="w-full h-full object-cover rounded-b-lg"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center justify-center gap-6 mt-12">
                        <button
                            onClick={prevSlide}
                            className="w-10 h-10 rounded-full bg-primary text-white hover:bg-blue-700 flex items-center justify-center transition-colors"
                            aria-label="Previous feature"
                        >
                            <ChevronLeft size={20} />
                        </button>

                        {/* Dots indicator */}
                        <div className="flex gap-2">
                            {features.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToSlide(index)}
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        index === currentIndex
                                            ? 'w-6 bg-primary'
                                            : 'w-2 bg-slate-300 hover:bg-slate-400'
                                    }`}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={nextSlide}
                            className="w-10 h-10 rounded-full bg-primary text-white hover:bg-blue-700 flex items-center justify-center transition-colors"
                            aria-label="Next feature"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default SoftwarePreview;